import { accountMapper } from '@services/mappers/accountMapper';
import {
  PaginatedTransactions,
  WithdrawalAccount,
  WithdrawalRequest,
  DepositDetails,
} from '@typeDefs/domain/entities/accounts';
import apiClient from './client';
import { parseSaleSubmitError } from './saleSubmitError';

export const accountService = {
  async getRecentTransactions(
    corporateClientId?: string,
    yearMonth?: string,
    pageSize?: number,
    pageNumber?: number,
  ): Promise<PaginatedTransactions> {
    const response = await apiClient.api.accountRecentTransactionsList({
      CorporateClientId: corporateClientId,
      YearMonth: yearMonth,
      PageSize: pageSize,
      PageNumber: pageNumber,
    });
    return accountMapper.paginatedTransactionsFromApi(response.data);
  },

  // Get withdrawal accounts
  async getWithdrawalAccounts(corporateClientId: string): Promise<WithdrawalAccount[]> {
    try {
      const response = await apiClient.api.accountWithdrawalAccountsList({
        corporateClientId: corporateClientId,
      });

      if (!response.data) {
        throw new Error('No withdrawal accounts data received');
      }

      return accountMapper.withdrawalAccountsFromDto(response.data);
    } catch (error) {
      console.error(
        'Error fetching withdrawal accounts:',
        error instanceof Error ? error.message : error,
      );
      throw new Error('Failed to fetch withdrawal accounts');
    }
  },

  // Submit withdrawal request
  //
  // `idempotencyKey` is a STABLE per-action key generated once per user action
  // (e.g. one click) and reused across retries/double-submits so the server
  // dedupes them. When supplied it is sent as the `Idempotency-Key` header.
  async submitWithdrawalRequest(
    request: WithdrawalRequest,
    idempotencyKey?: string,
  ): Promise<boolean> {
    try {
      // Map domain type to API command (with validation)
      const command = accountMapper.withdrawalRequestToCommand(request);

      // Use the generated API method. The trailing RequestParams arg carries the
      // stable idempotency key when provided.
      await apiClient.api.accountWithdrawalsCreate(
        command,
        idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : undefined,
      );

      // Map response (API returns void, mapper returns true for success)
      return accountMapper.withdrawalResponseFromApi();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error(
        'Error submitting withdrawal request:',
        error instanceof Error ? error.message : error,
      );
      // 409 = duplicate / in-flight same idempotency key. Surface a static
      // message (D4: web-client shows a fixed string, never the server body)
      // and do NOT auto-resubmit.
      if (error?.response?.status === 409) {
        throw new Error('Request is already in progress');
      }

      // ── 402 — the ONE relaxation of D4 (#161 / feature preservoir-ai#117) ──
      //
      // D4's rule is "show a fixed string, never the server body", and it holds
      // for every other status below. The 402 insufficient-funds response is the
      // sole exception, because the thing the user needs to know is a NUMBER the
      // server computed: how much of their balance is held back for accrued
      // fees. A fixed string cannot carry it, and this is the only path by which
      // it can reach the withdrawal screen at all.
      //
      // The relaxation is narrow by construction:
      //   • It parses through `parseSaleSubmitError`, whose CLOSED allowlist
      //     admits only recognised codes — so an unrecognised or absent code
      //     falls through to the generic message rather than echoing raw server
      //     detail. (The `detail` fallback inside the parser is reached only for
      //     an allowlisted code with no mapped copy, which cannot happen while
      //     `SALE_ERROR_MESSAGES` is a total `Record` over the union.)
      //   • It still THROWS. A refused withdrawal must never resolve `true`, and
      //     the failed XHR must stay visible to App Insights as a failed
      //     dependency — never converted into silent success.
      // `accountService.test.ts` T24 fences the boundary; T25 fences the throw.
      //
      // `submitWithdrawalRequest` has exactly one production caller
      // (`Withdrawal.tsx`), so the changed error surface is contained to that
      // page. The other methods on this service keep their fixed-string shape.
      if (error?.response?.status === 402) {
        throw parseSaleSubmitError(error, 'Failed to submit withdrawal request');
      }

      throw new Error('Failed to submit withdrawal request');
    }
  },

  // Get deposit details
  async getDepositDetails(corporateClientId?: string): Promise<DepositDetails> {
    try {
      const response = await apiClient.api.accountDepositDetailsList({
        corporateClientId: corporateClientId,
      });

      if (!response.data) {
        throw new Error('No deposit details data received');
      }

      return accountMapper.depositDetailsFromDto(response.data);
    } catch (error) {
      console.error(
        'Error fetching deposit details:',
        error instanceof Error ? error.message : error,
      );
      throw new Error('Failed to fetch deposit details');
    }
  },
};
